import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, AlertCircle, Check, BadgeCheck } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { AuthPageLayout } from '../../components/auth/AuthPageLayout';
import { ApiError } from '../../lib/api';

const slugify = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    organizationSlug: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName || !formData.organizationName) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the terms to continue');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await register({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        organization_name: formData.organizationName.trim(),
        organization_slug: formData.organizationSlug || slugify(formData.organizationName),
      });
      navigate('/', { replace: true });
    } catch (err: unknown) {
      if (err instanceof ApiError && typeof err.details?.detail === 'string') {
        setError(err.details.detail);
      } else if (err instanceof Error) {
        setError(err.message || 'Registration failed. Please try again.');
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'organizationName' && !slugTouched ? { organizationSlug: slugify(value) } : {}),
    }));
    if (field === 'organizationSlug') setSlugTouched(true);
    setError('');
  };

  const INPUT =
    'block w-full rounded-lg border border-slate-400 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-[#2fae2c] focus:outline-none focus:ring-2 focus:ring-[#40b93c]/30';

  const pwRules = [
    { ok: formData.password.length >= 8, label: '8+ characters' },
    { ok: /[A-Z]/.test(formData.password), label: 'Uppercase' },
    { ok: /[a-z]/.test(formData.password), label: 'Lowercase' },
    { ok: /\d/.test(formData.password), label: 'Number' },
  ];
  const strengthScore = pwRules.filter((rule) => rule.ok).length;
  const strengthLabel = ['Very weak', 'Weak', 'Fair', 'Strong', 'Excellent'][strengthScore];
  const strengthColor = ['bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-emerald-600'][strengthScore];

  return (
    <AuthPageLayout
      footer={
        <div className="space-y-1.5">
          <p className="text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-[#1f4778] hover:text-[#14315a] hover:underline">
              Sign in
            </Link>
          </p>
          <p className="text-xs text-slate-400">First user becomes organization admin by default.</p>
        </div>
      }
    >
      <div className="mb-4 space-y-2">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          <BadgeCheck className="h-3.5 w-3.5" /> Organization onboarding
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Create your workspace</h1>
        <p className="text-sm text-slate-500">Professional setup in under two minutes. You can invite teammates after sign in.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3.5">
        {error && (
          <div className="flex items-center gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <section className="space-y-2.5 rounded-xl border border-slate-300 bg-white p-3.5 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.10)]">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Profile</h2>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">First name</label>
              <input type="text" autoComplete="given-name" value={formData.firstName} onChange={handleInputChange('firstName')} className={INPUT} placeholder="Ava" required />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Last name</label>
              <input type="text" autoComplete="family-name" value={formData.lastName} onChange={handleInputChange('lastName')} className={INPUT} placeholder="Wells" required />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Work email</label>
            <input
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              className={INPUT}
              placeholder="you@company.com"
              required
            />
          </div>
        </section>

        <section className="space-y-2.5 rounded-xl border border-slate-300 bg-white p-3.5 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.10)]">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Workspace</h2>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Organization name</label>
            <input type="text" autoComplete="organization" value={formData.organizationName} onChange={handleInputChange('organizationName')} className={INPUT} placeholder="Northwind Labs" required />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Workspace slug</label>
            <div className="flex items-center rounded-lg border border-slate-400 bg-white px-3.5 py-2.5 shadow-sm">
              <span className="mr-1.5 text-sm text-slate-400">cosellus.ai/</span>
              <input
                type="text"
                value={formData.organizationSlug}
                onChange={handleInputChange('organizationSlug')}
                className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                placeholder="northwind-labs"
              />
            </div>
          </div>
        </section>

        <section className="space-y-2.5 rounded-xl border border-slate-300 bg-white p-3.5 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.10)]">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Security</h2>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={formData.password}
                onChange={handleInputChange('password')}
                className={`${INPUT} pr-10`}
                placeholder="Create a strong password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {formData.password && (
              <>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-300">
                  <div className={`h-full rounded-full transition-all ${strengthColor}`} style={{ width: `${(strengthScore / 4) * 100}%` }} />
                </div>
                <p className="mt-1 text-xs font-medium text-slate-500">Strength: {strengthLabel}</p>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {pwRules.map((r) => (
                    <span key={r.label} className={`flex items-center gap-1 text-xs ${r.ok ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <Check className="h-3 w-3" /> {r.label}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Confirm password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              className={INPUT}
              placeholder="Confirm password"
              required
            />
          </div>
        </section>

        <label className="flex items-start gap-2.5 rounded-lg bg-slate-50 px-3.5 py-3 text-sm ring-1 ring-slate-200 cursor-pointer">
          <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />
          <span className="text-slate-600 leading-snug">I agree to the terms of service and understand this workspace is organization-scoped.</span>
        </label>

        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#40b93c] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2f962d] hover:shadow-[0_8px_20px_rgba(64,185,60,0.35)] focus:outline-none focus:ring-2 focus:ring-[#40b93c] focus:ring-offset-2 disabled:translate-y-0 disabled:opacity-50"
        >
          {isLoading ? <LoadingSpinner size="sm" /> : 'Create account'}
        </button>
      </form>
    </AuthPageLayout>
  );
};

export default RegisterPage;
